import React, { useEffect, useState } from "react";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import { MaterialReactTable } from "material-react-table";
import { Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import userResources from "api/userResources";
import { selectUserInfo } from "../../../redux/userSlice";
import { useSelector } from "react-redux";
import { FaMailBulk, FaRegStar, FaStar, FaWhatsapp } from "react-icons/fa";
import Modal from "components/daisyUiMsgModal/Modal";

export default function FeedbackList() {
  const userInfo = useSelector(selectUserInfo);
  const [globalFilter, setGlobalFilter] = useState("");
  const [feedbackData, setFeedbackData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedExperience, setSelectedExperience] = useState(""); // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility

  const fetchFeedbacks = async (pageIndex, pageSize, globalFilter) => {
    setIsLoading(true);
    try {
      const response = await userResources.feedbackList(
        pageIndex || 0,
        pageSize || 5,
        globalFilter || ""
      );
      setFeedbackData(response.data.data);
      setTotalRows(response.data.paginate_data.total);
    } catch (error) {
      console.error("Error fetching Feedbacks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const loadingToastId = toast.loading("Deleting Feedback...");
    try {
      await userResources.feedbackDelete(id);
      toast.update(loadingToastId, {
        render: "Feedback deleted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      fetchFeedbacks(pagination.pageIndex, pagination.pageSize, globalFilter);
    } catch (error) {
      console.error("Error deleting Feedback:", error);
      toast.update(loadingToastId, {
        render: "Failed to delete Feedback. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      if (toast.isActive(loadingToastId)) {
        toast.dismiss(loadingToastId);
      }
    }
  };

  // Function to format date and time separately
  const formatDateAndTime = (timestamp) => {
    const date = new Date(timestamp);

    // Extract date in YYYY-MM-DD format
    const formattedDate = date.toISOString().split("T")[0];

    // Extract time in HH:MM:SS format
    const formattedTime = date.toTimeString().split(" ")[0];

    return { formattedDate, formattedTime };
  };

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value);
  };

  useEffect(() => {
    fetchFeedbacks(pagination.pageIndex, pagination.pageSize, globalFilter);
  }, [pagination, globalFilter]);

  const handlePaginationChange = (updatedPagination) => {
    setPagination(updatedPagination);
  };

  const handleModalOpen = (experience) => {
    setSelectedExperience(experience); // Set the travel experience text
    setIsModalOpen(true); // Open the modal
  };

  const handleModalClose = () => {
    setIsModalOpen(false); // Close the modal
    setSelectedExperience(""); // Reset the travel experience
  };

  const columns = [
    { accessorKey: "id", header: "Id" },
    { accessorKey: "name", header: "Name" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phone_number", header: "Phone Number" },
    {
      accessorKey: "rating",
      header: "Rating",
      Cell: ({ cell }) => {
        const rating = cell.getValue();
        const stars = Array(5)
          .fill(0)
          .map((_, index) => (
            <span key={index}>
              {index < rating ? (
                <FaStar className="text-yellow-500" />
              ) : (
                <FaRegStar className="text-gray-400" />
              )}
            </span>
          ));

        return <div className="flex">{stars}</div>;
      },
    },
    {
      accessorKey: "travel_experience",
      header: "Travel Experience",
      Cell: ({ row }) => (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleModalOpen(row.original.travel_experience)}
        >
          View Experience
        </button>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      Cell: ({ cell }) => {
        const date = new Date(cell.getValue());
        return (
          <>
            <div>Date: {date.toLocaleDateString()}</div>
            <div>Time: {date.toLocaleTimeString()}</div>
          </>
        );
      },
    },
    {
      accessorKey: "action",
      header: "Action",
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          {userInfo?.data?.role_id !==
            Number(process.env.REACT_APP_ROLE_EMPLOYEE) && (
            <ConfirmModal
              onConfirm={handleDelete}
              title="Delete Feedback"
              message="Are you sure you want to delete this Feedback?"
              employeeId={row.original.id}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <section className="mt-6">
      <ScrollToTopOnRouteChange />
      <MaterialReactTable
        columns={columns}
        data={feedbackData}
        state={{ pagination, isLoading }}
        enablePagination
        onPaginationChange={handlePaginationChange}
        manualPagination
        rowCount={totalRows}
        pageCount={Math.ceil(totalRows / pagination.pageSize)}
        isLoading={isLoading}
        enableColumnFilters={false}
        enableSorting={false}
        enableColumnOrdering={false}
        muiTablePaginationProps={{
          showFirstButton: true,
          showLastButton: true,
        }}
        enableGlobalFilter
        onGlobalFilterChange={handleGlobalFilterChange}
        globalFilter={globalFilter}
        enableRowActions
        renderRowActions={({ row }) => (
          <div className="flex flex-nowrap gap-4">
            <button
              className="text-xl text-primary"
              onClick={() => {
                const { formattedDate, formattedTime } = formatDateAndTime(
                  row.original.created_at
                );

                window.open(
                  `mailto:${row.original.email}?subject=Thank You for Your Feedback&body=Dear ${row.original.name},%0D%0A%0D%0A
        Thank you for taking the time to share your feedback with us. We truly appreciate your thoughts and insights.%0D%0A
        Below are the details of your feedback:%0D%0A%0D%0A
        - **Name**: ${row.original.name}%0D%0A
        - **Email**: ${row.original.email}%0D%0A
        - **Phone Number**: ${row.original.phone_number}%0D%0A
        - **Feedback Message**: ${row.original.feedback}%0D%0A
        - **Feedback Received On (Date)**: ${formattedDate}%0D%0A
        - **Feedback Received On (Time)**: ${formattedTime}%0D%0A%0D%0A
        We value your input and will review your feedback to improve our services. If you have any further suggestions or questions, please don’t hesitate to reach out.%0D%0A%0D%0A
        Best Regards,%0D%0A
        The Team at Demo Tours and Travel%0D%0A%0D%0A
        ---%0D%0A
        **Company Contact Information**:%0D%0A
        - **Website**: https://www.maharajaflights.com%0D%0A
        - **Toll Free Number**: +1 1888-678-1201%0D%0A
        - **Email**: katariatravel@gmail.com%0D%0A
        - **Address**: UNIT 12, 4550 EBENEZER RD BRAMPTON ONTARIO L6P 1H4, Canada%0D%0A`
                );
              }}
              title="Send Email"
            >
              <FaMailBulk />
            </button>

            <button
              className="text-xl text-success"
              onClick={() => {
                const { formattedDate, formattedTime } = formatDateAndTime(
                  row.original.created_at
                );

                window.open(
                  `https://wa.me/${row.original.phone_number.replace(
                    /\D/g,
                    ""
                  )}?text=Hello ${row.original.name},%0D%0A%0D%0A
        Thank you for sharing your feedback with us! We truly value your thoughts and suggestions.%0D%0A
        Below are the details of your feedback:%0D%0A%0D%0A
        - *Name*: ${row.original.name}%0D%0A
        - *Email*: ${row.original.email}%0D%0A
        - *Phone Number*: ${row.original.phone_number}%0D%0A
        - *Feedback Message*: ${row.original.feedback}%0D%0A
        - **Feedback Received On (Date)**: ${formattedDate}%0D%0A
        - **Feedback Received On (Time)**: ${formattedTime}%0D%0A%0D%0A
        We appreciate your input and will take it into consideration as we continue to improve our services. If you have any further suggestions or questions, please feel free to reach out.%0D%0A%0D%0A
        Best Regards,%0D%0A
        The Team at Demo Tours and Travel`
                );
              }}
            >
              <FaWhatsapp />
            </button>
          </div>
        )}
      />

      {/* DaisyUI Modal */}
      {isModalOpen && (
        <Modal
          heading="Travel Experience"
          paragraph={selectedExperience}
          close={handleModalClose}
        />
      )}
    </section>
  );
}
